import React, { useMemo, useState } from 'react';
import { Button, Empty, Space, Table } from 'antd';
import { CopyOutlined, FileAddOutlined } from '@ant-design/icons';
import { useI18n } from '../../i18n';
import WizardStepFilters from './WizardStepFilters';
import { buildProductsStepColumns } from './wizardStepColumns';
import { getQualifyingProductRecords, toSupplierProductRef, filterProductRows } from '../../utils/wizardHelpers';

/**
 * SelectionWizard step 1 (inquiry-module-redesign, Requirement 4): search
 * and multi-select products in the item's category. One row per (product,
 * supplier) Supplier_Product record — the same product offered by two
 * suppliers is two rows, not one row listing two supplier names — using
 * the search/filters and columns from the Products Module's Products tab
 * (Supplier Item Number, Product, Supplier, Average Item Price). Product
 * and Supplier are clickable (open their own page and return here after);
 * rows are otherwise chosen via row selection, no Actions column. Filters
 * mirror the Products tab's search and Type — Category is left out since
 * the step is already scoped to the item's own category, and Status is
 * left out since every qualifying record here is already selectable.
 *
 * "Add Product" / "Copy Existing" let the catalogue be extended without
 * leaving the wizard: both are handled by App.js (product creation logic
 * lives there), locked to the item's own category and requiring a price so
 * the result always qualifies as a selectable row here, then handed back
 * via `onReady` and auto-selected — see openWizardProductChooser /
 * openWizardCopyProduct in App.js.
 */
const PRODUCT_TYPE_OPTIONS = [
  { value: 'Standard', label: 'Standard Product' },
  { value: 'Custom', label: 'Custom Product' },
];

export default function ProductsStep({
  supplierProducts = [],
  products = [],
  categoryId,
  suppliers = [],
  selectedProducts = [],
  onChange,
  onOpenProduct,
  onOpenSupplier,
  onAddProduct,
  onCopyProduct,
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const productTypeById = useMemo(() => (
    new Map(products.map((product) => [product.id, product.productType]))
  ), [products]);

  const rows = useMemo(() => {
    const qualifying = getQualifyingProductRecords(supplierProducts, categoryId).map((sp) => ({
      ...toSupplierProductRef(sp, suppliers),
      productType: productTypeById.get(sp.productId) || null,
    }));
    // A row just added via Add Product / Copy Existing is always shown and
    // selected here even on the rare chance it doesn't (yet) qualify —
    // e.g. a supplier product edited elsewhere to drop its price — rather
    // than silently vanishing from a step that already has it selected.
    const qualifyingIds = new Set(qualifying.map((row) => row.supplierProductId));
    const extras = selectedProducts.filter((ref) => !qualifyingIds.has(ref.supplierProductId));
    return [...extras, ...qualifying];
  }, [supplierProducts, categoryId, suppliers, productTypeById, selectedProducts]);

  const filtered = useMemo(() => (
    filterProductRows(rows, search)
      .filter((row) => typeFilter === 'all' || row.productType === typeFilter)
  ), [rows, search, typeFilter]);

  const selectedKeys = selectedProducts.map((ref) => ref.supplierProductId);

  // rowSelection.onChange reports the full selected set for the CURRENTLY
  // VISIBLE (filtered) rows only — rows hidden by search/type filters
  // keep whatever selection state they already had.
  const handleSelectionChange = (_keys, newlySelectedVisibleRows) => {
    const visibleKeys = new Set(filtered.map((row) => row.supplierProductId));
    const kept = selectedProducts.filter((ref) => !visibleKeys.has(ref.supplierProductId));
    onChange([...kept, ...newlySelectedVisibleRows]);
  };

  const handleOpenProduct = (row) => {
    const product = products.find((p) => p.id === row.productId);
    if (product) onOpenProduct?.(product);
  };

  const handleOpenSupplier = (row) => {
    const supplier = suppliers.find((s) => s.id === row.supplierId);
    if (supplier) onOpenSupplier?.(supplier);
  };

  // Called with the newly created/copied product + its Supplier_Product row
  // once App.js finishes the create flow — selects it immediately so the
  // user doesn't have to hunt for what they just added.
  const handleProductReady = (product, supplierProduct) => {
    if (!supplierProduct) return;
    onChange([...selectedProducts, {
      ...toSupplierProductRef(supplierProduct, suppliers),
      productType: product.productType,
    }]);
  };

  const columns = buildProductsStepColumns({ t, onOpenProduct: handleOpenProduct, onOpenSupplier: handleOpenSupplier });

  return (
    <div>
      {(onAddProduct || onCopyProduct) && (
        <Space style={{ marginBottom: 12 }}>
          {onAddProduct && (
            <Button icon={<FileAddOutlined />} onClick={() => onAddProduct(handleProductReady)}>
              {t('Add Product')}
            </Button>
          )}
          {onCopyProduct && (
            <Button icon={<CopyOutlined />} onClick={() => onCopyProduct(handleProductReady)}>
              {t('Copy Existing (Custom)')}
            </Button>
          )}
        </Space>
      )}
      <WizardStepFilters
        t={t}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('Search by product number, name, or supplier')}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        typeOptions={PRODUCT_TYPE_OPTIONS}
      />
      {rows.length === 0 ? (
        <Empty description={t('No products available for this category')} />
      ) : filtered.length === 0 ? (
        <Empty description={t("No results for '{{query}}'", { query: search })} />
      ) : (
        <Table
          rowKey="supplierProductId"
          columns={columns}
          dataSource={filtered}
          pagination={false}
          size="middle"
          bordered
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: handleSelectionChange,
          }}
        />
      )}
    </div>
  );
}
